const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// ─── SYSTEM PROMPTS ────────────────────────────────────────────────────────────
const LEAD_SYSTEM_PROMPT = `You are a seasoned cruise travel specialist with 10+ years of experience booking cruises for all types of clients. You work with the Avoya Travel Network and pride yourself on building genuine relationships — not just making sales. Your emails are:
- Warm, personal, and conversational (never corporate or stiff)
- Specific to the client's interest and budget — you never send generic blasts
- Focused on VALUE of booking with a human agent vs. booking direct online
- Always under 250 words
- Ending with ONE low-pressure call to action (a brief call or reply)

 Do NOT include any sign-off, signature, or closing like "Warm regards" or agent name — end the email body after the call to action. The signature will be added separately.

Never mention competitors. Never sound like a robot. Sound like a trusted friend who happens to be a cruise expert. Never use em dashes (—) in any response — use commas, periods, or rewrite the sentence instead.`

const JOURNEY_SYSTEM_PROMPT = `You are a meticulous cruise travel specialist who takes pride in making every client feel like a VIP from booking day to embarkation day. Your pre-departure touchpoint calendars are:
- Timed precisely based on real cruise industry booking windows (excursions open ~90 days out, dining ~60-90 days, docs needed ~45 days, etc.)
- Personalized to the specific client details provided
- Written in a warm, concise, actionable tone — not corporate
- Practical and genuinely helpful, not just filler check-ins

Each message should feel like it came from a trusted advisor, not an automated system. Never use em dashes (—) in any response. Use commas, periods, or rewrite the sentence structure instead.`

const COMPARE_SYSTEM_PROMPT = `You are a trusted cruise specialist who helps clients make confident booking decisions. You write comparison summaries that are warm, clear, and specific — never corporate or robotic. You highlight the real differences that matter to this particular client, not just specs and prices. You always end with a clear, confident recommendation that feels like advice from a friend, not a sales pitch. Never use em dashes (—) in any response. Use commas, periods, or rewrite the sentence structure instead.`

// ─── PROMPT BUILDERS ───────────────────────────────────────────────────────────
const buildLeadPrompt = (formData) => `
  Write a personalized first-contact email for a cruise lead with these details:
  - Client Name: ${formData.name}
  - Cruise Interest: ${formData.interest}
  - Budget Range: ${formData.budget}
  - Personal Detail / Notes: ${formData.details || 'None provided'}

  The email should feel hand-written, not templated. Reference their specific interest naturally. Highlight 1-2 concrete benefits of booking through a dedicated cruise specialist vs. going direct. Close with a soft CTA to hop on a quick call or simply reply with questions.

  Do NOT include any sign-off, signature, or closing like "Warm regards" or agent name — end the email body after the call to action. The signature will be added separately.
`;

const buildJourneyPrompt = (formData) => {
  const sailDate = new Date(formData.sailDate);
  const formatDate = (date) => date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  const calcDate = (daysOut) => {
    const d = new Date(sailDate);
    d.setDate(d.getDate() - daysOut);
    return formatDate(d);
  };

  return `
  Create a pre-departure touchpoint calendar for a cruise client with these details:
  - Sail Date: ${formatDate(sailDate)}
  - Client Detail 1: ${formData.detail1 || 'Not specified'}
  - Client Detail 2: ${formData.detail2 || 'Not specified'}
  - Client Detail 3: ${formData.detail3 || 'Not specified'}

  Here are the exact calculated send dates based on the sail date above:
  - Immediately after booking: today
  - 90 days before sailing: ${calcDate(90)}
  - 75 days before sailing: ${calcDate(75)}
  - 45 days before sailing: ${calcDate(45)}
  - 14 days before sailing: ${calcDate(14)}
  - 2 days before sailing: ${calcDate(2)}

  Generate EXACTLY 6 touchpoint messages using those milestones in order. Use real cruise industry timing for key milestones (excursion booking windows, dining reservations, document deadlines, online check-in, packing reminders, bon voyage). Personalize each message using the client details above.

Do NOT include any sign-off or signature in the messages — end each message after the call to action. The signature will be added separately.

  Respond ONLY with a valid JSON array. No markdown. No preamble.
  Format: [
    {"title": "Brief phase name (3-5 words)", "timing": "e.g., 90 days before sailing (October 3, 2025)", "message": "The full ready-to-send message to the client (2-4 sentences, warm and specific)"}
  ]`;
};

const buildComparePrompt = (formData) => `
  Write a narrative cruise comparison for a client named ${formData.clientName}.
  
  Client Profile: ${formData.clientNotes}

  Option 1:
  - Line: ${formData.option1.line}
  - Ship: ${formData.option1.ship}
  - Itinerary: ${formData.option1.itinerary}
  - Date: ${formData.option1.date}
  - Cabin: ${formData.option1.cabin}
  - Price: ${formData.option1.price}

  Option 2:
  - Line: ${formData.option2.line}
  - Ship: ${formData.option2.ship}
  - Itinerary: ${formData.option2.itinerary}
  - Date: ${formData.option2.date}
  - Cabin: ${formData.option2.cabin}
  - Price: ${formData.option2.price}

  Option 3:
  - Line: ${formData.option3.line}
  - Ship: ${formData.option3.ship}
  - Itinerary: ${formData.option3.itinerary}
  - Date: ${formData.option3.date}
  - Cabin: ${formData.option3.cabin}
  - Price: ${formData.option3.price}

  Instructions:
  1. Write a 300-400 word narrative comparison. Do NOT use a table.
  2. Use warm, conversational language.
  3. Highlight the specific trade-offs (e.g., newer ship vs. better itinerary vs. price value) based on the client's profile.
  4. End with a clearly labeled "OUR RECOMMENDATION" section that definitively picks one option and explains exactly why it fits their profile best.

Do NOT include any sign-off or signature — end after the recommendation. The signature will be added separately.
`;

// ─── MAIN EXPORT ───────────────────────────────────────────────────────────────
export const generateContent = async (type, formData) => {
  const isJourney = type === 'journey';
  const isCompare = type === 'compare';
  
  try {
    const response = await fetch('/api/claude', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
  model: 'claude-sonnet-4-20250514',  // This is correct
  max_tokens: isJourney ? 2500 : 1500,
  system: isCompare ? COMPARE_SYSTEM_PROMPT : (isJourney ? JOURNEY_SYSTEM_PROMPT : LEAD_SYSTEM_PROMPT),
  messages: [{ 
    role: 'user', 
    content: isCompare ? buildComparePrompt(formData) : (isJourney ? buildJourneyPrompt(formData) : buildLeadPrompt(formData)) 
  }]
})
    });

    // Check if we got HTML instead of JSON (404 page)
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('text/html')) {
      throw new Error('Function endpoint not found - deployment issue');
    }

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err?.error?.message || `API Error: ${response.status}`);
    }

    const data = await response.json();
    let text = data.content[0].text;

    if (isJourney) {
      text = text.replace(/```json\n?|\n?```/g, '').trim();
      return JSON.parse(text);
    }
    return text;
  } catch (error) {
    console.error("Claude API error:", error.message);
    throw error;
  }
};
// ─── MOCK DATA (dev only) ──────────────────────────────────────────────────────
export const getMockData = (type) => {
  if (type === 'lead') {
    return `Subject: Your Caribbean Cruise — Let's Make It Happen!\n\nHi Sarah,\n\nI'm so glad you reached out! Planning a cruise is one of my favorite things to do, and I'd love to help make yours absolutely unforgettable.\n\nBooking directly with the cruise line might seem simpler, but here's what you'd be missing: a real human advocate in your corner. I handle everything from finding the best cabin placement to monitoring prices after you book — and if anything goes sideways, you have me, not a call center.\n\nI'd love to learn more about what you're envisioning. Would you be open to a quick 10-minute call this week?\n\nWarm regards,\nYour Cruise Specialist`;
  }
  if (type === 'journey') {
    return [
      { title: "Welcome Aboard!", timing: "Day of booking", message: "Your reservation is confirmed! I'll be with you every step of the way. Keep an eye on your inbox — I'll be sending helpful tips as your sail date approaches." },
      { title: "Shore Excursion Window Opens", timing: "90 days before sailing", message: "Great news — your shore excursion booking window is now open! I've put together my top picks for your ports. Want me to go ahead and reserve any?" },
      { title: "Dining & Spa Reservations", timing: "75 days before sailing", message: "Specialty dining and spa times are filling up fast. Let me know if you'd like a reservation at any of the premium restaurants — I'll handle it for you." },
      { title: "Documents & Passport Check", timing: "45 days before sailing", message: "Quick check-in: make sure your passport is valid for at least 6 months after your return date. Also a great time to download the cruise line's app if you haven't already!" },
      { title: "Packing & Final Prep", timing: "14 days before sailing", message: "Almost time! I've put together a quick packing list based on your itinerary and the ship's dress code guidelines. Reply if you have any last-minute questions." },
      { title: "Bon Voyage!", timing: "2 days before sailing", message: "You're almost there! Everything is in order. Have the most amazing trip — I can't wait to hear all about it when you're back." }
    ];
  }
  if (type === 'compare') {
    return `Hi Sarah,\n\nI've spent some time looking at the three options we discussed, and each one offers something unique for your anniversary celebration. \n\nOption 1 on Royal Caribbean's Icon of the Seas is the "wow" choice. Since you mentioned loving fine dining, this ship is unparalleled with over 40 restaurants. However, it is the most premium price point. \n\nOption 2 with Celebrity Apex offers a more sophisticated, "modern luxury" vibe. The Infinite Veranda cabin would be incredible for your morning coffee while sailing the Mediterranean. It's a bit more refined and less "theme park" than Icon.\n\nOption 3 on Virgin Voyages is the most value-driven but still high-end. It's adults-only, which might be perfect for an anniversary, and all specialty dining is included in that $1,849 price.\n\nOUR RECOMMENDATION:\nI strongly recommend Option 2 (Celebrity Apex). Given this is your 10th anniversary and you value fine dining and a relaxed but upscale atmosphere, Celebrity hits the "sweet spot." It’s significantly more sophisticated than Royal, yet more inclusive and anniversary-focused than Virgin. Plus, the Mediterranean itinerary for that date is spectacular.\n\nWhat do you think of the Celebrity option?`;
  }
  return null;
};
